import React from 'react';
import { Link } from 'react-router-dom';
import Disclaimer from '../../components/Disclaimer';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { ArrowLeft, Wallet, Clock, CheckCircle } from 'lucide-react';

const RetirerGainsParis = () => {
  return (
    <div className="min-h-screen bg-black">
      <Disclaimer />
      <Header />
      <div className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <Link
            to="/guides"
            data-discover="true"
            className="inline-flex items-center gap-2 text-toxic-green hover:text-toxic-cyan mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour aux guides
          </Link>

          <div className="mb-8">
            <div className="inline-flex items-center gap-2 mb-4 px-4 py-2 glass rounded-full border border-toxic-green/30">
              <Wallet className="w-5 h-5 text-toxic-green" />
              <span className="text-sm text-toxic-green font-semibold">Guide pratique</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4 text-white">
              Comment retirer vos gains
            </h1>
            <p className="text-xl text-white/70">
              Guide complet sur les processus de retrait et les meilleures pratiques
            </p>
          </div>

          <article className="glass-strong rounded-2xl p-8 md:p-12 border border-white/10 space-y-8 text-white/80 leading-relaxed">
            <section>
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">Comprendre le processus de retrait</h2>
              <p className="mb-4">
                Retirer vos gains est l'aboutissement naturel d'une session de paris réussie. Cependant, ce processus peut sembler 
                complexe si vous ne comprenez pas les règles et les délais associés. Les opérateurs agréés en France doivent respecter 
                des procédures strictes pour garantir la sécurité des transactions et la conformité réglementaire.
              </p>
              <p className="mb-4">
                La première chose à comprendre est que vous ne pouvez retirer que les fonds disponibles sur votre compte. Les fonds 
                bloqués par des conditions de mise de bonus, des paris en cours, ou des vérifications en attente ne peuvent pas être 
                retirés. Vérifiez toujours votre solde disponible avant de demander un retrait pour éviter les déceptions.
              </p>
              <p>
                Les opérateurs agréés proposent généralement plusieurs méthodes de retrait, chacune avec ses propres caractéristiques 
                en termes de rapidité, de limites, et de frais. Comprendre ces différences vous permet de choisir la méthode qui 
                correspond le mieux à vos besoins et à votre situation.
              </p>
            </section>

            <section>
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">Vérifications préalables</h2>
              <p className="mb-4">
                Avant de pouvoir retirer, votre compte doit être entièrement vérifié. Cela inclut la vérification d'identité (KYC) 
                que vous avez effectuée lors de la création de votre compte. Si cette vérification n'est pas complète, votre demande 
                de retrait sera mise en attente jusqu'à ce que tous les documents soient validés.
              </p>
              <p className="mb-4">
                Certains opérateurs exigent également une vérification supplémentaire pour les premiers retraits ou les retraits 
                importants. Cela peut inclure une vérification de l'adresse, une confirmation téléphonique, ou une demande de documents 
                supplémentaires. Ces vérifications sont normales et font partie des mesures de sécurité standard.
              </p>
              <p className="mb-4">
                Assurez-vous que toutes les conditions de mise de vos bonus sont remplies avant de demander un retrait. Si vous avez 
                utilisé un bonus de bienvenue, vous devez généralement miser le montant du bonus un certain nombre de fois avant de 
                pouvoir retirer les gains associés. Vérifiez votre progression dans votre espace client.
              </p>
              <p>
                Enfin, vérifiez que votre méthode de retrait est bien configurée et à jour. Si vous souhaitez retirer sur une carte 
                bancaire, assurez-vous que les informations de la carte sont correctes et que la carte n'a pas expiré. Pour les 
                virements bancaires, vérifiez que votre IBAN est correct et que le compte bancaire est toujours actif.
              </p>
            </section>

            <section>
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">Méthodes de retrait disponibles</h2>
              <div className="mb-6">
                <h3 className="text-xl font-semibold text-white mb-3">Retrait par virement bancaire</h3>
                <div className="flex items-start gap-3 mb-3">
                  <Clock className="w-5 h-5 text-toxic-green flex-shrink-0 mt-1" />
                  <p>
                    Les virements bancaires sont généralement la méthode la plus fiable pour les retraits importants. Les délais varient 
                    entre 24 et 72 heures après validation de votre demande, selon l'opérateur et votre banque. Les limites sont souvent 
                    élevées, permettant de retirer plusieurs milliers d'euros en une seule transaction.
                  </p>
                </div>
                <p className="mb-4">
                  Les frais sont généralement minimes ou inexistants pour les virements bancaires. Votre banque peut facturer des frais 
                  de réception, mais cela dépend de votre contrat bancaire. Les opérateurs agréés n'appliquent généralement pas de frais 
                  supplémentaires pour cette méthode.
                </p>
                <p>
                  Pour utiliser cette méthode, vous devrez fournir votre IBAN et votre BIC lors de la configuration de votre compte. 
                  Assurez-vous que ces informations sont exactes, car une erreur peut retarder considérablement votre retrait ou même 
                  le faire échouer.
                </p>
              </div>

              <div className="mb-6">
                <h3 className="text-xl font-semibold text-white mb-3">Retrait sur carte bancaire</h3>
                <p className="mb-4">
                  Certains opérateurs permettent de retirer directement sur la carte bancaire utilisée pour le dépôt. Cette méthode est 
                  généralement rapide, avec des fonds disponibles sous 24 à 48 heures. Cependant, les limites peuvent être plus 
                  restrictives que pour les virements bancaires.
                </p>
                <p>
                  Cette méthode est pratique si vous avez utilisé une carte bancaire pour vos dépôts et souhaitez récupérer vos gains 
                  rapidement. Cependant, tous les opérateurs ne proposent pas cette option, et certaines cartes peuvent ne pas être 
                  éligibles pour les retraits.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-white mb-3">Retrait vers portefeuille électronique</h3>
                <p className="mb-4">
                  Si vous avez utilisé un portefeuille électronique pour vos dépôts, vous pouvez généralement retirer vers le même 
                  portefeuille. Les retraits vers PayPal, Skrill, ou Neteller sont souvent très rapides, parfois en quelques heures.
                </p>
                <p>
                  Cependant, vous devrez ensuite transférer les fonds de votre portefeuille électronique vers votre compte bancaire, 
                  ce qui peut prendre du temps supplémentaire et impliquer des frais. Cette méthode peut être pratique pour les petits 
                  retraits rapides, mais moins idéale pour les montants importants.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">Délais de traitement</h2>
              <p className="mb-4">
                Les délais de retrait varient considérablement selon la méthode choisie et l'opérateur. Les retraits vers portefeuilles 
                électroniques sont généralement les plus rapides, souvent traités en quelques heures. Les retraits par virement bancaire 
                prennent généralement entre 24 et 72 heures après validation de votre demande.
              </p>
              <p className="mb-4">
                Le premier retrait peut prendre plus de temps car il nécessite souvent des vérifications supplémentaires. Les opérateurs 
                doivent s'assurer que vous êtes bien le propriétaire du compte et que toutes les conditions sont remplies. Une fois ces 
                vérifications effectuées, les retraits suivants sont généralement plus rapides.
              </p>
              <p className="mb-4">
                Les week-ends et les jours fériés peuvent ralentir le traitement des retraits, surtout pour les virements bancaires. 
                Si vous demandez un retrait un vendredi après-midi, il est probable qu'il ne soit traité qu'à partir du lundi suivant. 
                Planifiez vos retraits en conséquence si vous avez besoin d'accéder rapidement à vos fonds.
              </p>
              <p>
                Certains opérateurs proposent des retraits express pour un coût supplémentaire. Ces retraits sont traités en priorité 
                et peuvent être disponibles en quelques heures au lieu de plusieurs jours. Cette option peut être intéressante si vous 
                avez besoin d'accéder rapidement à vos gains, mais évaluez si le coût supplémentaire en vaut la peine.
              </p>
            </section>

            <section>
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">Limites de retrait</h2>
              <p className="mb-4">
                Tous les opérateurs imposent des limites de retrait, à la fois par transaction et par période (quotidienne, hebdomadaire, 
                mensuelle). Ces limites varient considérablement selon l'opérateur et votre niveau de vérification. Les clients VIP ou 
                vérifiés peuvent bénéficier de limites plus élevées.
              </p>
              <p className="mb-4">
                Les limites minimales de retrait sont généralement faibles, souvent autour de 10€ à 20€. Cela permet de retirer même de 
                petits gains. Les limites maximales par transaction peuvent aller de quelques centaines d'euros à plusieurs milliers, 
                selon l'opérateur et la méthode choisie.
              </p>
              <p>
                Si vous souhaitez retirer un montant supérieur à la limite maximale, vous devrez effectuer plusieurs retraits. Cependant, 
                cela peut prendre plus de temps et impliquer des vérifications supplémentaires. Pour les retraits très importants, 
                contactez le service client pour discuter des options disponibles.
              </p>
            </section>

            <section>
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">Conseils pour des retraits réussis</h2>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-toxic-green flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-white mb-2">Vérifiez votre solde disponible</h3>
                    <p>
                      Avant de demander un retrait, assurez-vous que votre solde disponible correspond au montant que vous souhaitez 
                      retirer. Les fonds bloqués par des bonus ou des paris en cours ne peuvent pas être retirés.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-toxic-green flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-white mb-2">Vérifiez vos informations bancaires</h3>
                    <p>
                      Assurez-vous que votre IBAN, BIC, et autres informations bancaires sont corrects et à jour. Une erreur peut 
                      retarder considérablement votre retrait ou le faire échouer, nécessitant de recommencer le processus.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-toxic-green flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-white mb-2">Respectez les conditions de mise</h3>
                    <p>
                      Si vous avez utilisé un bonus, assurez-vous que toutes les conditions de mise sont remplies avant de demander 
                      un retrait. Sinon, votre demande sera refusée et vous devrez compléter les mises requises.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-toxic-green flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-white mb-2">Planifiez vos retraits</h3>
                    <p>
                      Si vous avez besoin d'accéder rapidement à vos gains, planifiez votre retrait en tenant compte des délais de 
                      traitement. Évitez de demander un retrait juste avant un week-end si vous avez besoin des fonds rapidement.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">En résumé</h2>
              <p className="mb-4">
                Retirer vos gains est un processus simple une fois que vous comprenez les règles et les délais associés. Choisissez la 
                méthode de retrait qui correspond le mieux à vos besoins en termes de rapidité, de limites, et de coûts. Vérifiez 
                toujours que votre compte est entièrement vérifié et que toutes les conditions sont remplies avant de demander un retrait.
              </p>
              <p>
                La patience est souvent nécessaire, surtout pour le premier retrait qui nécessite des vérifications supplémentaires. 
                Une fois ces vérifications effectuées, les retraits suivants sont généralement plus rapides et plus simples. N'hésitez 
                pas à contacter le service client si vous avez des questions ou si votre retrait prend plus de temps que prévu. Les 
                opérateurs agréés offrent généralement un support réactif pour vous aider à récupérer vos gains rapidement et en toute 
                sécurité.
              </p>
            </section>
          </article>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default RetirerGainsParis;

